import {
  formatAuthMessage,
  getDevelopmentAuthCredentials,
  getOAuthCodeFromUrl,
  getOAuthProviderUnavailableMessage,
  getOAuthSignInOptions,
  getTestAuthCredentials,
  isAppleOAuthConfigured,
  isDevelopmentAutoLoginDisabled,
  isE2EAuthFlowStubEnabled,
  isE2EDevPassthroughEnabled,
  normalizeAuthEmail,
  type SupportedOAuthProvider,
} from "@/services/authHelpers"
import { getNotificationPreferences } from "@/services/notificationPreferences"
import { syncOneSignalSession } from "@/services/notifications/oneSignalClient"
import {
  getDefaultOAuthReturnPath,
  isSupabaseOAuthClientConfigured,
  sanitizeOAuthReturnPath,
  startSupabaseOAuthSignIn,
} from "@/services/oauthServerClient"
import { getSupabaseClient, isSupabaseConfigured } from "@/services/supabaseClient"
import AsyncStorage from "@react-native-async-storage/async-storage"
import type { Session, User } from "@supabase/supabase-js"
import * as Linking from "expo-linking"
import * as WebBrowser from "expo-web-browser"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Platform } from "react-native"

WebBrowser.maybeCompleteAuthSession()

declare const process: {
  env?: Record<string, string | undefined>
}

export type AuthProfile = {
  id: string
  email: string | null
  display_name: string | null
  avatar_url: string | null
  settings: Record<string, unknown>
}

type AuthContextValue = {
  configured: boolean
  loading: boolean
  actionLoading: boolean
  session: Session | null
  user: User | null
  profile: AuthProfile | null
  message: string
  signInWithPassword: (email: string, password: string) => Promise<void>
  signUpWithPassword: (email: string, password: string, displayName?: string) => Promise<void>
  sendMagicLink: (email: string) => Promise<void>
  sendPasswordReset: (email: string) => Promise<void>
  signInWithOAuth: (provider: SupportedOAuthProvider) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  clearMessage: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)
const SESSION_EXPIRY_REFRESH_BUFFER_MS = 60_000
const E2E_AUTH_STUB_STORAGE_KEY = "emo:e2e-auth-flow-stub-session"

type E2EStoredSession = {
  email?: string
  provider?: "email" | "google"
}

function getRedirectTo() {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    return `${window.location.origin}/auth`
  }

  return Linking.createURL("/auth")
}

function getOAuthReturnTo() {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return getDefaultOAuthReturnPath()
  }

  const params = new URLSearchParams(window.location.search)
  return sanitizeOAuthReturnPath(params.get("returnTo") ?? params.get("redirect"))
}

async function clearSupabaseAuthStorage() {
  if (Platform.OS === "web") {
    if (typeof localStorage === "undefined") return

    const keysToRemove: string[] = []
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)
      if (key?.startsWith("sb-") || key?.startsWith("supabase.auth.")) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
    return
  }

  const keys = await AsyncStorage.getAllKeys()
  const authKeys = keys.filter((key) => key.startsWith("sb-") || key.startsWith("supabase.auth."))
  if (authKeys.length > 0) {
    await AsyncStorage.multiRemove(authKeys)
  }
}

function readE2EStoredSession(): E2EStoredSession | null {
  if (Platform.OS !== "web" || typeof localStorage === "undefined") return null

  try {
    const rawSession = localStorage.getItem(E2E_AUTH_STUB_STORAGE_KEY)
    if (!rawSession) return null

    const parsed = JSON.parse(rawSession) as E2EStoredSession
    return parsed && typeof parsed === "object" ? parsed : null
  } catch {
    localStorage.removeItem(E2E_AUTH_STUB_STORAGE_KEY)
    return null
  }
}

function writeE2EStoredSession(session: E2EStoredSession) {
  if (Platform.OS !== "web" || typeof localStorage === "undefined") return
  localStorage.setItem(E2E_AUTH_STUB_STORAGE_KEY, JSON.stringify(session))
}

function clearE2EStoredSession() {
  if (Platform.OS !== "web" || typeof localStorage === "undefined") return
  localStorage.removeItem(E2E_AUTH_STUB_STORAGE_KEY)
}

async function resolveFreshSession(session: Session | null) {
  if (!session) return null

  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : null
  if (!expiresAtMs || expiresAtMs - Date.now() > SESSION_EXPIRY_REFRESH_BUFFER_MS) {
    return session
  }

  const client = getSupabaseClient()
  if (!client) return session

  const { data, error } = await client.auth.refreshSession()
  if (error) throw error

  return data.session
}

function isDevelopmentRuntime() {
  return process.env?.NODE_ENV === "development"
}

function createE2EDevSession(
  emailOverride?: string,
  provider: "email" | "google" = "email",
): Session {
  const { email } = getTestAuthCredentials()
  const sessionEmail = normalizeAuthEmail(emailOverride || email)
  const displayName =
    provider === "google" ? "Google Test User" : sessionEmail.split("@")[0] || "E2E Test User"
  const now = new Date().toISOString()

  return {
    access_token: "e2e-mock-token",
    refresh_token: "e2e-mock-refresh",
    expires_in: 3600,
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    token_type: "bearer",
    user: {
      id: "e2e-dev-user",
      email: sessionEmail,
      user_metadata: {
        username: provider === "google" ? "google_test_user" : "e2e_test_user",
        full_name: displayName,
        display_name: displayName,
      },
      aud: "authenticated",
      confirmed_at: now,
      created_at: now,
      updated_at: now,
      app_metadata: { provider, providers: [provider] },
      role: "authenticated",
    },
  } as Session
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const configured = isSupabaseConfigured()
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<AuthProfile | null>(null)
  const [message, setMessage] = useState("")
  const exchangedOAuthCodes = useRef(new Set<string>())

  const client = getSupabaseClient()
  const user = session?.user ?? null
  const authFlowStubEnabled = isE2EAuthFlowStubEnabled()

  const applyE2EAuthSession = useCallback(
    (messageText: string, email?: string, provider?: "email" | "google") => {
      const nextSession = createE2EDevSession(email, provider)
      setSession(nextSession)
      setProfile({
        id: nextSession.user.id,
        email: nextSession.user.email ?? null,
        display_name:
          (nextSession.user.user_metadata?.display_name as string | undefined) ??
          nextSession.user.email?.split("@")[0] ??
          "E2E Test User",
        avatar_url: null,
        settings: {
          notifications: { push: false, email: true },
          publicProfile: { enabled: false },
        },
      })
      writeE2EStoredSession({ email: nextSession.user.email ?? undefined, provider })
      setMessage(`Test auth stub: ${messageText}`)
    },
    [],
  )

  const refreshProfile = useCallback(async () => {
    if (!client) {
      setProfile(null)
      return
    }

    const {
      data: { user: currentUser },
    } = await client.auth.getUser()

    if (!currentUser) {
      setProfile(null)
      return
    }

    const fallbackProfile = {
      id: currentUser.id,
      email: currentUser.email ?? null,
      display_name:
        (currentUser.user_metadata?.display_name as string | undefined) ??
        (currentUser.user_metadata?.full_name as string | undefined) ??
        currentUser.email?.split("@")[0] ??
        null,
      avatar_url: (currentUser.user_metadata?.avatar_url as string | undefined) ?? null,
      settings: {
        notifications: { push: false, email: true },
        publicProfile: { enabled: false },
      },
    }

    const { data, error } = await client
      .from("profiles")
      .upsert(fallbackProfile, { onConflict: "id" })
      .select("id,email,display_name,avatar_url,settings")
      .single()

    if (error) {
      setProfile(fallbackProfile)
      setMessage(`Signed in, but profile sync failed: ${error.message}`)
      return
    }

    setProfile(data as AuthProfile)
  }, [applyE2EAuthSession, authFlowStubEnabled, client])

  useEffect(() => {
    if (!client) {
      if (isDevelopmentRuntime() && isE2EDevPassthroughEnabled()) {
        setSession(createE2EDevSession())
        setMessage("Using test auth passthrough session for local test automation.")
      } else if (isDevelopmentRuntime() && authFlowStubEnabled) {
        const storedSession = readE2EStoredSession()
        if (storedSession) {
          applyE2EAuthSession(
            "restored signed-in browser automation session.",
            storedSession.email,
            storedSession.provider,
          )
        }
      }
      setLoading(false)
      return
    }

    let mounted = true

    async function initializeSession() {
      const { data, error } = await client.auth.getSession()
      if (!mounted) return

      if (error) setMessage(formatAuthMessage(error))

      const freshSession = await resolveFreshSession(data.session)
      if (freshSession) {
        setSession(freshSession)
        return
      }

      if (!isDevelopmentRuntime()) {
        setSession(null)
        return
      }

      if (isE2EDevPassthroughEnabled()) {
        setSession(createE2EDevSession())
        setMessage("Using test auth passthrough session for local test automation.")
        return
      }

      if (isDevelopmentAutoLoginDisabled()) {
        setSession(null)
        return
      }

      const credentials = getDevelopmentAuthCredentials()
      const { data: signInData, error: signInError } =
        await client.auth.signInWithPassword(credentials)
      if (signInError) {
        setMessage(formatAuthMessage(signInError))
        setSession(null)
        return
      }

      setSession(signInData.session)
      setMessage(`Signed in as ${credentials.email} for local development.`)
    }

    initializeSession()
      .catch((error) => {
        if (!mounted) return
        setMessage(formatAuthMessage(error))
        setSession(null)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (!nextSession && isDevelopmentRuntime() && isE2EDevPassthroughEnabled()) {
        return
      }

      setSession(nextSession)
      setLoading(false)
      if (!nextSession) {
        setProfile(null)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [client])

  const exchangeOAuthCode = useCallback(
    async (url: string | null | undefined) => {
      if (!client) return

      const code = getOAuthCodeFromUrl(url)
      if (!code || exchangedOAuthCodes.current.has(code)) return

      exchangedOAuthCodes.current.add(code)
      setActionLoading(true)
      setMessage("")

      const { data, error } = await client.auth.exchangeCodeForSession(code)
      setActionLoading(false)

      if (error) {
        setMessage(formatAuthMessage(error))
        return
      }

      setSession(data.session)
      setMessage("Signed in. Proofs and tasks can now sync to Supabase.")
    },
    [client],
  )

  useEffect(() => {
    if (!client || Platform.OS === "web") {
      return
    }

    Linking.getInitialURL()
      .then(exchangeOAuthCode)
      .catch((error) => {
        console.warn("Failed to read initial auth callback URL", error)
      })

    const subscription = Linking.addEventListener("url", ({ url }) => {
      exchangeOAuthCode(url)
    })

    return () => {
      subscription.remove()
    }
  }, [client, exchangeOAuthCode])

  useEffect(() => {
    if (session?.user) {
      refreshProfile()
      return
    }

    setProfile(null)
  }, [refreshProfile, session?.user])

  useEffect(() => {
    if (loading) {
      return
    }

    const pushEnabled = getNotificationPreferences(profile).push
    const pushUser = pushEnabled && session?.user ? { id: session.user.id } : null

    syncOneSignalSession(pushUser).catch((error) => {
      console.warn("Failed to sync OneSignal session", error)
    })
  }, [loading, profile, session?.user])

  async function runAuthAction(action: () => Promise<void>) {
    if (!client && !authFlowStubEnabled) {
      setMessage("Supabase is not configured for this build.")
      return
    }

    setActionLoading(true)
    setMessage("")
    try {
      await action()
    } catch (error) {
      setMessage(formatAuthMessage(error))
    } finally {
      setActionLoading(false)
    }
  }

  const value = useMemo<AuthContextValue>(
    () => ({
      configured,
      loading,
      actionLoading,
      session,
      user,
      profile,
      message,
      signInWithPassword: async (email, password) =>
        runAuthAction(async () => {
          if (authFlowStubEnabled) {
            applyE2EAuthSession("signed in with password for browser automation.", email)
            return
          }

          const { data, error } = await client!.auth.signInWithPassword({
            email: normalizeAuthEmail(email),
            password,
          })
          if (error) throw error
          setSession(data.session)
          setMessage("Signed in. Proofs and tasks can now sync to Supabase.")
        }),
      signUpWithPassword: async (email, password, displayName) =>
        runAuthAction(async () => {
          if (authFlowStubEnabled) {
            applyE2EAuthSession("created account for browser automation.", email)
            return
          }

          const { data, error } = await client!.auth.signUp({
            email: normalizeAuthEmail(email),
            password,
            options: {
              data: {
                display_name: displayName?.trim() || normalizeAuthEmail(email).split("@")[0],
              },
            },
          })
          if (error) throw error
          setSession(data.session)
          setMessage(
            data.session
              ? "Account created and signed in."
              : "Account created. Check your email if confirmation is required.",
          )
        }),
      sendMagicLink: async (email) =>
        runAuthAction(async () => {
          if (authFlowStubEnabled) {
            setMessage(
              `Test auth stub: magic link sent to ${normalizeAuthEmail(
                email,
              )}. Open it on this device to finish signing in.`,
            )
            return
          }

          const { error } = await client!.auth.signInWithOtp({
            email: normalizeAuthEmail(email),
            options: { emailRedirectTo: getRedirectTo() },
          })
          if (error) throw error
          setMessage("Magic link sent. Open it on this device to finish signing in.")
        }),
      sendPasswordReset: async (email) =>
        runAuthAction(async () => {
          if (authFlowStubEnabled) {
            setMessage(
              `Test auth stub: password reset link sent to ${normalizeAuthEmail(
                email,
              )}. Open it to choose a new password.`,
            )
            return
          }

          const { error } = await client!.auth.resetPasswordForEmail(normalizeAuthEmail(email), {
            redirectTo: getRedirectTo(),
          })
          if (error) throw error
          setMessage("Password reset link sent. Open it to choose a new password.")
        }),
      signInWithOAuth: async (provider) =>
        runAuthAction(async () => {
          if (provider === "apple" && !isAppleOAuthConfigured()) {
            setMessage(getOAuthProviderUnavailableMessage(provider))
            return
          }

          if (authFlowStubEnabled) {
            if (provider === "google") {
              applyE2EAuthSession(
                "Google OAuth completed for browser automation.",
                undefined,
                "google",
              )
              return
            }

            setMessage(getOAuthProviderUnavailableMessage(provider))
            return
          }

          if (provider === "google" && Platform.OS === "web" && isSupabaseOAuthClientConfigured()) {
            await startSupabaseOAuthSignIn(getOAuthReturnTo())
            return
          }

          const { data, error } = await client!.auth.signInWithOAuth(
            getOAuthSignInOptions(provider, getRedirectTo()),
          )
          if (error) throw error

          if (Platform.OS !== "web" && data.url) {
            const result = await WebBrowser.openAuthSessionAsync(data.url, getRedirectTo())
            if (result.type === "success") {
              await exchangeOAuthCode(result.url)
            }
          } else if (Platform.OS === "web" && data.url) {
            window.location.assign(data.url)
          }
        }),
      signOut: async () =>
        runAuthAction(async () => {
          if (authFlowStubEnabled) {
            await clearSupabaseAuthStorage()
            clearE2EStoredSession()
            setSession(null)
            setProfile(null)
            setMessage("Test auth stub: signed out.")
            return
          }

          const { error } = await client!.auth.signOut()
          if (error) throw error
          await clearSupabaseAuthStorage()
          setSession(null)
          setProfile(null)
          setMessage("Signed out.")
        }),
      refreshProfile,
      clearMessage: () => setMessage(""),
    }),
    [
      actionLoading,
      applyE2EAuthSession,
      authFlowStubEnabled,
      client,
      configured,
      exchangeOAuthCode,
      loading,
      message,
      profile,
      refreshProfile,
      session,
      user,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider")
  }

  return value
}
